import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { faExclamationCircle } from "@fortawesome/pro-regular-svg-icons";
import { useKeyDown } from "@react-hooks-library/core";
import { isEqual } from "lodash-es";
import { useForm } from "react-hook-form";

import { usePlaygroundContext } from "src/agents/AgentVersionPage/PlaygroundContext";
import type { AgentPlaygroundFormValues } from "src/api/agents/execution";
import { useAgentVersion } from "src/api/agents/management";
import { Schema } from "src/api/flowTypes";
import { AgentFailedBanner } from "src/base-components/agents/AgentFailedBanner";
import {
  ExecutionLogView,
  getInputTokenCount,
  TestInputAccordion,
} from "src/base-components/agents/ExecutionLogView";
import { Callout } from "src/design-system/Callout";
import { EmptyState } from "src/design-system/EmptyState";
import { toastActions } from "src/design-system/Toast/utils";
import { EnumValueObject } from "src/schema/schemaMappingUtils";
import {
  AgentSchemaPropertyUI,
  convertAgentSchemaBEtoUI,
} from "src/utils/agents/agentSchemaUtils";
import { ErrorBoundary } from "src/utils/ErrorBoundary";

type AgentPlaygroundContentProps = {
  agentId: string;
  versionId: string;
};

const generateDefaultValues = (
  properties: AgentSchemaPropertyUI[],
): AgentPlaygroundFormValues => {
  const structuredInput: Record<string, unknown> = {};
  for (const prop of properties) {
    const type = prop.type?.[0] ?? "string";
    switch (type) {
      case "string":
        structuredInput[prop.fieldName] = "";
        break;
      case "number":
      case "integer":
        structuredInput[prop.fieldName] = undefined;
        break;
      case "boolean":
        structuredInput[prop.fieldName] = false;
        break;
      case "enum":
        structuredInput[prop.fieldName] = undefined;
        break;
      case "date":
      case "datetime":
        structuredInput[prop.fieldName] = undefined;
        break;
      case "array":
        structuredInput[prop.fieldName] = prop.required ? "[]" : "";
        break;
      case "object":
        structuredInput[prop.fieldName] = prop.required ? "{}" : "";
        break;
      case "file[]":
        structuredInput[prop.fieldName] = [];
        break;
    }
  }
  return {
    structuredInput,
    instructions: "",
  };
};

/**
 * Parses JSON string values in structuredInput back to arrays/objects.
 * CodeEditor stores array/object fields as strings; this converts them
 * before JSON.stringify to avoid double-encoding.
 */
const parseJsonFieldsInStructuredInput = (
  values: AgentPlaygroundFormValues,
  properties: AgentSchemaPropertyUI[],
): AgentPlaygroundFormValues => {
  const parsedStructuredInput: Record<string, unknown> = {};

  // Build a set of field names that are array or object type
  const objectAndArrayFieldNames = new Set(
    properties
      .filter((p) => {
        const type = p.type?.[0];
        return type === "array" || type === "object";
      })
      .map((p) => p.fieldName),
  );

  for (const [key, value] of Object.entries(values.structuredInput)) {
    if (typeof value === "string" && objectAndArrayFieldNames.has(key)) {
      // Handle empty strings for optional fields - treat as undefined
      if (!value.trim()) {
        parsedStructuredInput[key] = undefined;
      } else {
        parsedStructuredInput[key] = JSON.parse(value);
      }
    } else {
      parsedStructuredInput[key] = value;
    }
  }

  return {
    ...values,
    structuredInput: parsedStructuredInput,
  };
};

export const AgentPlaygroundContent: React.FC<AgentPlaygroundContentProps> = ({
  agentId,
  versionId,
}) => {
  const [isInputAccordionExpanded, setIsInputAccordionExpanded] =
    useState(true);
  const [hasAttemptedSubmit, setHasAttemptedSubmit] = useState(false);

  const versionQuery = useAgentVersion(agentId, versionId);
  // Get session state and invoke from playground context
  const {
    sessionState,
    rawSessionData,
    invoke: invokeAgent,
    registerStartTest,
  } = usePlaygroundContext();

  const inputSchema = versionQuery.data?.configuration.input_schema;

  const properties = useMemo(
    () =>
      convertAgentSchemaBEtoUI(inputSchema as Schema | null)?.properties ?? [],
    [inputSchema],
  );

  const playgroundForm = useForm<AgentPlaygroundFormValues>({
    defaultValues: generateDefaultValues(properties),
    mode: "onChange",
  });

  const prevPropertiesRef = useRef<AgentSchemaPropertyUI[]>(properties);

  // Reset form when input schema changes, preserving values for unchanged fields
  useEffect(() => {
    const prevProperties = prevPropertiesRef.current;
    const currentValues = playgroundForm.getValues();
    const newDefaults = generateDefaultValues(properties);
    const currentStructuredInput = currentValues.structuredInput;
    const newStructuredInput = newDefaults.structuredInput;

    // Merge: keep current value if field exists with same type (and still valid for enums), else use default
    const mergedStructuredInput: Record<string, unknown> = {};
    for (const prop of properties) {
      const prevProp = prevProperties.find(
        (p) => p.fieldName === prop.fieldName,
      );
      const propType = prop.type?.[0] ?? "string";
      const isSameType = prevProp?.type?.[0] === propType;
      const currentValue = currentStructuredInput[prop.fieldName];
      const enumValues = (prop.enum as EnumValueObject[] | undefined)?.map(
        (e) => e.value,
      );
      const isValidEnumValue =
        propType !== "enum" ||
        (enumValues ?? []).includes(currentValue as string);

      if (
        isSameType &&
        isValidEnumValue &&
        prop.fieldName in currentStructuredInput
      ) {
        mergedStructuredInput[prop.fieldName] = currentValue;
      } else {
        mergedStructuredInput[prop.fieldName] =
          newStructuredInput[prop.fieldName];
      }
    }

    playgroundForm.reset({
      structuredInput: mergedStructuredInput,
      instructions: currentValues.instructions,
    });
    setHasAttemptedSubmit(false);
    prevPropertiesRef.current = properties;
  }, [properties, playgroundForm]);

  // Expand accordion when session becomes inactive (failed or cancelled only)
  const prevIsActiveRef = useRef(sessionState.status === "active");
  useEffect(() => {
    if (prevIsActiveRef.current && sessionState.status !== "active") {
      const isSuccess =
        sessionState.status === "stopped" &&
        sessionState.substatus === "success";
      if (!isSuccess) {
        setIsInputAccordionExpanded(true);
      }
    }
    prevIsActiveRef.current = sessionState.status === "active";
  }, [sessionState.status]);

  const handleSubmit = useCallback(
    (values: AgentPlaygroundFormValues) => {
      // Parse array/object fields that were stored as JSON strings in CodeEditor
      const parsedValues = parseJsonFieldsInStructuredInput(values, properties);

      // Auto-collapse the input section when test starts
      setIsInputAccordionExpanded(false);

      invokeAgent.mutate(
        {
          inputSchema: (inputSchema as Record<string, unknown>) ?? null,
          submittedInput: parsedValues,
        },
        {
          onError: () => {
            // Context clears session on error, just show toast and expand accordion
            setIsInputAccordionExpanded(true);
            toastActions.failure({
              title: "Failed to run Agent",
              description: "Please try again",
            });
          },
        },
      );
    },
    [invokeAgent, properties, inputSchema],
  );

  const handleStartTest = useCallback(() => {
    setHasAttemptedSubmit(true);
    playgroundForm.handleSubmit(
      (values) => {
        handleSubmit(values);
      },
      () => {
        setIsInputAccordionExpanded(true);
      },
    )();
  }, [playgroundForm, handleSubmit]);

  // Register handleStartTest with context so header button can trigger it
  useEffect(() => {
    registerStartTest(handleStartTest);
  }, [registerStartTest, handleStartTest]);

  // Cmd/Ctrl + Enter triggers start test when a playground input is focused
  const playgroundContainerRef = useRef<HTMLDivElement>(null);
  useKeyDown(
    ["Enter"],
    (e) => {
      if ((e.metaKey || e.ctrlKey) && sessionState.status !== "active") {
        e.preventDefault();
        handleStartTest();
      }
    },
    { target: playgroundContainerRef },
  );

  return (
    <div ref={playgroundContainerRef} className="flex h-full w-full flex-col">
      <ErrorBoundary
        fallbackErrorContent={
          <div className="flex flex-1 items-center justify-center p-5">
            <EmptyState
              description="Something went wrong. Please close and reopen the panel to try again."
              headline="Playground error"
              icon={faExclamationCircle}
              variant="error"
            />
          </div>
        }
      >
        <div className="flex h-full min-h-0 w-full flex-1 flex-col pr-5 pl-5">
          {/* Config updated after test started banner.
             We deep-compare the config objects instead of comparing etags because
             publishing a version bumps the etag even when the config content is
             unchanged. Deep equality ensures the banner only appears when the
             configuration has actually changed since the test session started. */}
          {sessionState.status !== "idle" &&
            rawSessionData?.agent_version_config != null &&
            versionQuery.data?.configuration != null &&
            !isEqual(
              rawSessionData.agent_version_config,
              versionQuery.data.configuration,
            ) && (
              <div className="mt-4 flex-shrink-0">
                <Callout type="info">
                  <span className="font-inter-medium-12px">
                    The config has been updated since this test started. Run
                    again to test with the latest changes.
                  </span>
                </Callout>
              </div>
            )}

          {/* Success banner */}
          {sessionState.status === "stopped" &&
            sessionState.substatus === "success" && (
              <div className="mt-4 flex-shrink-0">
                <Callout type="success">
                  <span className="font-inter-medium-12px">
                    Agent test run was successful
                  </span>
                </Callout>
              </div>
            )}

          {/* Failed banner */}
          {sessionState.status === "stopped" &&
            sessionState.substatus === "failed" && (
              <AgentFailedBanner
                terminationReason={sessionState.terminationReason}
                terminationMessage={sessionState.terminationMessage}
              />
            )}

          {/* Cancelled banner */}
          {sessionState.status === "stopped" &&
            sessionState.substatus === "cancelled" && (
              <div className="mt-4 flex-shrink-0">
                <Callout type="neutral">
                  <span className="font-inter-medium-12px">
                    Session stopped
                  </span>
                </Callout>
              </div>
            )}

          {/*
            Single test input + execution log. The accordion scrolls inline
            with the messages and is disabled while the session is active.
          */}
          <ExecutionLogView
            key={
              sessionState.status === "idle" ? "idle" : sessionState.sessionId
            }
            sessionState={sessionState}
            testInputHeader={
              <TestInputAccordion
                control={playgroundForm.control}
                dataLoc="test-input-accordion"
                disabled={sessionState.status === "active"}
                expanded={isInputAccordionExpanded}
                properties={properties}
                showErrors={hasAttemptedSubmit}
                tokenCount={
                  sessionState.status !== "idle"
                    ? getInputTokenCount(sessionState.sessionMessages)
                    : null
                }
                onExpandedChange={setIsInputAccordionExpanded}
                defaultOpen
              />
            }
          />
        </div>
      </ErrorBoundary>
    </div>
  );
};
